import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import { RefTypeItem } from '@sd-portal/shared/ref-type-item';
import { auditLogAdapter } from '../shared-audit.adapters';
import * as actions from './person-profile.actions';
import {
  PersonProfileSkeleton,
  PersonProfileAudit,
  BusinessPersonAudit,
  PhoneNumberAudit,
  EmailAddressAudit,
  AddressAudit,
  EmergencyContactAudit,
  PersonAttributeAudit,
  StaffInfoAudit,
  PreferenceAudit,
  ExecutiveAssociateAudit,
  CrewMemberTypeRatingAudit,
  TaxInfoAudit,
  PersonStaffGroupAudit,
  PassengerAudit
} from '../../+models/person-profile-audit.model';

// States
interface PersonProfileSkeletonState extends EntityState<PersonProfileSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface PersonProfileAuditState extends EntityState<PersonProfileAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface BusinessPersonAuditState extends EntityState<BusinessPersonAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PhoneNumberAuditState extends EntityState<PhoneNumberAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface EmailAddressAuditState extends EntityState<EmailAddressAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AddressAuditState extends EntityState<AddressAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface EmergencyContactAuditState extends EntityState<EmergencyContactAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface EmergencyContactPhoneNumberAuditState extends EntityState<PhoneNumberAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PersonAttributeAuditState extends EntityState<PersonAttributeAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PersonStaffGroupAuditState extends EntityState<PersonStaffGroupAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface StaffInfoAuditState extends EntityState<StaffInfoAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PreferenceAuditState extends EntityState<PreferenceAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ExecutiveAssociateAuditState extends EntityState<ExecutiveAssociateAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ExecutiveAssociatePhoneNumberAuditState extends EntityState<PhoneNumberAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ExecutiveAssociateEmailAuditState extends EntityState<EmailAddressAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface CrewMemberTypeRatingAuditState extends EntityState<CrewMemberTypeRatingAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface CrewMemberTypeRatingType extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface TaxInfoAuditState extends EntityState<TaxInfoAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PassengerAuditState extends EntityState<PassengerAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

// Adapters
const personProfileSkeletonAdapter = createEntityAdapter<PersonProfileSkeleton>({
  selectId: p => p.personId
});
const crewMemberTypeRatingTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});

// Entity Selectors
export const personProfileSkeletonSelectors = personProfileSkeletonAdapter.getSelectors();
export const personProfileAuditSelectors = auditLogAdapter.getSelectors();
export const businessPersonAuditSelectors = auditLogAdapter.getSelectors();
export const phoneNumberAuditSelectors = auditLogAdapter.getSelectors();
export const emailAddressAuditSelectors = auditLogAdapter.getSelectors();
export const addressAuditSelectors = auditLogAdapter.getSelectors();
export const emergencyContactAuditSelectors = auditLogAdapter.getSelectors();
export const emergencyContactPhoneNumberAuditSelectors = auditLogAdapter.getSelectors();
export const personAttributeAuditSelectors = auditLogAdapter.getSelectors();
export const personStaffGroupAuditSelectors = auditLogAdapter.getSelectors();
export const staffInfoAuditSelectors = auditLogAdapter.getSelectors();
export const preferenceAuditSelectors = auditLogAdapter.getSelectors();
export const executiveAssociatonAuditSelectors = auditLogAdapter.getSelectors();
export const executiveAssociatonPhoneNumberAuditSelectors = auditLogAdapter.getSelectors();
export const executiveAssociatonEmailAuditSelectors = auditLogAdapter.getSelectors();
export const crewMemberTypeRatingAuditSelectors = auditLogAdapter.getSelectors();
export const crewMemberTypeRatingTypeSelectors = crewMemberTypeRatingTypeAdapter.getSelectors();
export const taxInfoAuditSelectors = auditLogAdapter.getSelectors();
export const passengerAuditSelectors = auditLogAdapter.getSelectors();

// Initial States
const personProfileSkeletonInitialState = personProfileSkeletonAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});
const personProfileAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const businessPersonAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const phoneNumberAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const emailAddressAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const addressAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const emergencyContactAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const emergencyContactPhoneNumberAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const personAttributeAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const personStaffGroupAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const staffInfoAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const preferenceAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const executiveAssociatonAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const executiveAssociatonPhoneNumberAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const executiveAssociatonEmailAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const crewMemberTypeRatingAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const crewMemberTypeRatingTypeInitialState = crewMemberTypeRatingTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});
const taxInfoAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const passengerAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

export interface State {
  personProfileSkeleton: PersonProfileSkeletonState;
  personProfileAudit: PersonProfileAuditState;
  businessPersonAudit: BusinessPersonAuditState;
  phoneNumberAudit: PhoneNumberAuditState;
  emailAddressAudit: EmailAddressAuditState;
  addressAudit: AddressAuditState;
  emergencyContactAudit: EmergencyContactAuditState;
  emergencyContactPhoneNumberAudit: EmergencyContactPhoneNumberAuditState;
  personAttributeAudit: PersonAttributeAuditState;
  personStaffGroupAudit: PersonStaffGroupAuditState;
  staffInfoAudit: StaffInfoAuditState;
  preferenceCateringAudit: PreferenceAuditState;
  preferenceGroundTransportationAudit: PreferenceAuditState;
  preferenceHotelAudit: PreferenceAuditState;
  executiveAssociateAudit: ExecutiveAssociateAuditState;
  executiveAssociatePhoneNumberAudit: ExecutiveAssociatePhoneNumberAuditState;
  executiveAssociateEmailAudit: ExecutiveAssociateEmailAuditState;
  crewMemberTypeRatingAudit: CrewMemberTypeRatingAuditState;
  taxInfoAudit: TaxInfoAuditState;
  passengerAudit: PassengerAuditState;
  crewMemberTypeRatingTypes: CrewMemberTypeRatingType;
}

const initialState: State = {
  personProfileSkeleton: personProfileSkeletonInitialState,
  personProfileAudit: personProfileAuditInitialState,
  businessPersonAudit: businessPersonAuditInitialState,
  phoneNumberAudit: phoneNumberAuditInitialState,
  emailAddressAudit: emailAddressAuditInitialState,
  addressAudit: addressAuditInitialState,
  emergencyContactAudit: emergencyContactAuditInitialState,
  emergencyContactPhoneNumberAudit: emergencyContactPhoneNumberAuditInitialState,
  personAttributeAudit: personAttributeAuditInitialState,
  personStaffGroupAudit: personStaffGroupAuditInitialState,
  staffInfoAudit: staffInfoAuditInitialState,
  preferenceCateringAudit: preferenceAuditInitialState,
  preferenceGroundTransportationAudit: preferenceAuditInitialState,
  preferenceHotelAudit: preferenceAuditInitialState,
  executiveAssociateAudit: executiveAssociatonAuditInitialState,
  executiveAssociatePhoneNumberAudit: executiveAssociatonPhoneNumberAuditInitialState,
  executiveAssociateEmailAudit: executiveAssociatonEmailAuditInitialState,
  crewMemberTypeRatingAudit: crewMemberTypeRatingAuditInitialState,
  crewMemberTypeRatingTypes: crewMemberTypeRatingTypeInitialState,
  taxInfoAudit: taxInfoAuditInitialState,
  passengerAudit: passengerAuditInitialState
};

const personProfileReducer = createReducer<State>(
  initialState,

  on(actions.ResetPersonProfile, () => initialState),

  // Skeleton
  on(actions.LoadPersonProfileAuditSkeleton, state => ({
    ...state,

    personProfileSkeleton: {
      ...state.personProfileSkeleton,
      loading: true
    }
  })),

  on(actions.PersonProfileAuditSkeletonLoaded, (state, action) => ({
    ...state,

    personProfileSkeleton: {
      ...state.personProfileSkeleton,
      ...personProfileSkeletonAdapter.setAll([action.payload], state.personProfileSkeleton),
      loaded: true,
      loading: false
    }
  })),

  // PersonProfile
  on(actions.LoadPersonProfileAudit, (state, action) => ({
    ...state,

    personProfileAudit: {
      ...state.personProfileAudit,
      lastAuditLogId:
        state.personProfileAudit.selectedId === action.personId
          ? state.personProfileAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PersonProfileAuditLoaded, (state, action) => ({
    ...state,

    personProfileAudit: {
      ...state.personProfileAudit,
      ...(!state.personProfileAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.personProfileAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.personProfileAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // BusinessPerson
  on(actions.LoadBusinessPersonAudit, (state, action) => ({
    ...state,

    businessPersonAudit: {
      ...state.businessPersonAudit,
      lastAuditLogId:
        state.businessPersonAudit.selectedId === action.businessPersonId
          ? state.businessPersonAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.BusinessPersonAuditLoaded, (state, action) => ({
    ...state,

    businessPersonAudit: {
      ...state.businessPersonAudit,
      ...(!state.businessPersonAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.businessPersonAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.businessPersonAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // PhoneNumber
  on(actions.LoadPhoneNumberAudit, (state, action) => ({
    ...state,

    phoneNumberAudit: {
      ...state.phoneNumberAudit,
      lastAuditLogId:
        state.phoneNumberAudit.selectedId === action.phoneNumberId
          ? state.phoneNumberAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PhoneNumberAuditLoaded, (state, action) => ({
    ...state,

    phoneNumberAudit: {
      ...state.phoneNumberAudit,
      ...(!state.phoneNumberAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.phoneNumberAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.phoneNumberAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // EmailAddress
  on(actions.LoadEmailAddressAudit, (state, action) => ({
    ...state,

    emailAddressAudit: {
      ...state.emailAddressAudit,
      lastAuditLogId:
        state.emailAddressAudit.selectedId === action.emailAddressId
          ? state.emailAddressAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.EmailAddressAuditLoaded, (state, action) => ({
    ...state,

    emailAddressAudit: {
      ...state.emailAddressAudit,
      ...(!state.emailAddressAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.emailAddressAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.emailAddressAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // Address
  on(actions.LoadAddressAudit, (state, action) => ({
    ...state,

    addressAudit: {
      ...state.addressAudit,
      lastAuditLogId:
        state.addressAudit.selectedId === action.addressId
          ? state.addressAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AddressAuditLoaded, (state, action) => ({
    ...state,

    addressAudit: {
      ...state.addressAudit,
      ...(!state.addressAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.addressAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.addressAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // EmergencyContact
  on(actions.LoadEmergencyContactAudit, (state, action) => ({
    ...state,

    emergencyContactAudit: {
      ...state.emergencyContactAudit,
      lastAuditLogId:
        state.emergencyContactAudit.selectedId === action.emergencyContactId
          ? state.emergencyContactAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.EmergencyContactAuditLoaded, (state, action) => ({
    ...state,

    emergencyContactAudit: {
      ...state.emergencyContactAudit,
      ...(!state.emergencyContactAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.emergencyContactAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.emergencyContactAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // EmergencyContactPhoneNumber
  on(actions.LoadEmergencyContactPhoneNumberAudit, (state, action) => ({
    ...state,

    emergencyContactPhoneNumberAudit: {
      ...state.emergencyContactPhoneNumberAudit,
      lastAuditLogId:
        state.emergencyContactPhoneNumberAudit.selectedId === action.emergencyContactPhoneNumberId
          ? state.emergencyContactPhoneNumberAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.EmergencyContactPhoneNumberAuditLoaded, (state, action) => ({
    ...state,

    emergencyContactPhoneNumberAudit: {
      ...state.emergencyContactPhoneNumberAudit,
      ...(!state.emergencyContactPhoneNumberAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.emergencyContactPhoneNumberAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.emergencyContactPhoneNumberAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // PersonAttribute
  on(actions.LoadPersonAttributeAudit, (state, action) => ({
    ...state,

    personAttributeAudit: {
      ...state.personAttributeAudit,
      lastAuditLogId:
        state.personAttributeAudit.selectedId === action.personAttributeId
          ? state.personAttributeAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PersonAttributeAuditLoaded, (state, action) => ({
    ...state,

    personAttributeAudit: {
      ...state.personAttributeAudit,
      ...(!state.personAttributeAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.personAttributeAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.personAttributeAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // Person Staff Group
  on(actions.LoadPersonStaffGroupAudit, (state, action) => ({
    ...state,

    personStaffGroupAudit: {
      ...state.personStaffGroupAudit,
      lastAuditLogId:
        state.personStaffGroupAudit.selectedId === action.personStaffGroupId
          ? state.personStaffGroupAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PersonStaffGroupAuditLoaded, (state, action) => ({
    ...state,

    personStaffGroupAudit: {
      ...state.personStaffGroupAudit,
      ...(!state.personStaffGroupAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.personStaffGroupAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.personStaffGroupAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(
        ...action.audits.resultSet.map((r: { auditLogId: string | number }) => +r.auditLogId)
      )
    }
  })),

  // StaffInfo
  on(actions.LoadStaffInfoAudit, (state, action) => ({
    ...state,

    staffInfoAudit: {
      ...state.staffInfoAudit,
      lastAuditLogId:
        state.staffInfoAudit.selectedId === action.staffInfoId
          ? state.staffInfoAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.StaffInfoAuditLoaded, (state, action) => ({
    ...state,

    staffInfoAudit: {
      ...state.staffInfoAudit,
      ...(!state.staffInfoAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.staffInfoAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.staffInfoAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // Preferences
  on(actions.ResetPreferenceCatering, state => ({
    ...state,

    preferenceCateringAudit: preferenceAuditInitialState
  })),

  on(actions.LoadPreferenceCateringAudit, (state, action) => ({
    ...state,

    preferenceCateringAudit: {
      ...state.preferenceCateringAudit,
      lastAuditLogId:
        state.preferenceCateringAudit.selectedId === action.preferenceId
          ? state.preferenceCateringAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PreferenceCateringAuditLoaded, (state, action) => ({
    ...state,

    preferenceCateringAudit: {
      ...state.preferenceCateringAudit,
      ...(!state.preferenceCateringAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.preferenceCateringAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.preferenceCateringAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadPreferenceGroundTransportationAudit, (state, action) => ({
    ...state,

    preferenceGroundTransportationAudit: {
      ...state.preferenceGroundTransportationAudit,
      lastAuditLogId:
        state.preferenceGroundTransportationAudit.selectedId === action.preferenceId
          ? state.preferenceCateringAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PreferenceGroundTransportationAuditLoaded, (state, action) => ({
    ...state,

    preferenceGroundTransportationAudit: {
      ...state.preferenceGroundTransportationAudit,
      ...(!state.preferenceGroundTransportationAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.preferenceGroundTransportationAudit)
        : auditLogAdapter.addMany(
            action.audits.resultSet,
            state.preferenceGroundTransportationAudit
          )),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadPreferenceHotelAudit, (state, action) => ({
    ...state,

    preferenceHotelAudit: {
      ...state.preferenceHotelAudit,
      lastAuditLogId:
        state.preferenceHotelAudit.selectedId === action.preferenceId
          ? state.preferenceCateringAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PreferenceHotelAuditLoaded, (state, action) => ({
    ...state,

    preferenceHotelAudit: {
      ...state.preferenceHotelAudit,
      ...(!state.preferenceHotelAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.preferenceHotelAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.preferenceHotelAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // ExecutiveAssociate
  on(actions.LoadExecutiveAssociateAudit, (state, action) => ({
    ...state,

    executiveAssociateAudit: {
      ...state.executiveAssociateAudit,
      lastAuditLogId:
        state.executiveAssociateAudit.selectedId === action.executiveAssociateId
          ? state.executiveAssociateAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ExecutiveAssociateAuditLoaded, (state, action) => ({
    ...state,

    executiveAssociateAudit: {
      ...state.executiveAssociateAudit,
      ...(!state.executiveAssociateAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.executiveAssociateAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.executiveAssociateAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // ExecutiveAssociate Phone Number
  on(actions.LoadExecutiveAssociatePhoneNumberAudit, (state, action) => ({
    ...state,

    executiveAssociatePhoneNumberAudit: {
      ...state.executiveAssociatePhoneNumberAudit,
      lastAuditLogId:
        state.executiveAssociatePhoneNumberAudit.selectedId ===
        action.executiveAssociatePhoneNumberId
          ? state.executiveAssociatePhoneNumberAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ExecutiveAssociatePhoneNumberAuditLoaded, (state, action) => ({
    ...state,

    executiveAssociatePhoneNumberAudit: {
      ...state.executiveAssociatePhoneNumberAudit,
      ...(!state.executiveAssociatePhoneNumberAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.executiveAssociatePhoneNumberAudit)
        : auditLogAdapter.addMany(
            action.audits.resultSet,
            state.executiveAssociatePhoneNumberAudit
          )),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // ExecutiveAssociate Email
  on(actions.LoadExecutiveAssociateEmailAudit, (state, action) => ({
    ...state,

    executiveAssociateEmailAudit: {
      ...state.executiveAssociateEmailAudit,
      lastAuditLogId:
        state.executiveAssociateEmailAudit.selectedId === action.executiveAssociateEmailId
          ? state.executiveAssociateEmailAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ExecutiveAssociateEmailAuditLoaded, (state, action) => ({
    ...state,

    executiveAssociateEmailAudit: {
      ...state.executiveAssociateEmailAudit,
      ...(!state.executiveAssociateEmailAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.executiveAssociateEmailAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.executiveAssociateEmailAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // CrewMemberTypeRating
  on(actions.LoadCrewMemberTypeRatingAudit, (state, action) => ({
    ...state,

    crewMemberTypeRatingAudit: {
      ...state.crewMemberTypeRatingAudit,
      lastAuditLogId:
        state.crewMemberTypeRatingAudit.selectedId === action.crewMemberTypeRatingId
          ? state.crewMemberTypeRatingAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.CrewMemberTypeRatingAuditLoaded, (state, action) => ({
    ...state,

    crewMemberTypeRatingAudit: {
      ...state.crewMemberTypeRatingAudit,
      ...(!state.crewMemberTypeRatingAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.crewMemberTypeRatingAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.crewMemberTypeRatingAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadCrewMemberTypeRatingTypes, state => ({
    ...state,

    crewMemberTypeRatingTypes: {
      ...state.crewMemberTypeRatingTypes,
      loading: true
    }
  })),

  on(actions.CrewMemberTypeRatingTypesLoaded, (state, action) => ({
    ...state,

    crewMemberTypeRatingTypes: {
      ...state.crewMemberTypeRatingTypes,
      ...crewMemberTypeRatingTypeAdapter.setAll(action.payload, state.crewMemberTypeRatingTypes),
      loading: false,
      loaded: true
    }
  })),

  // TaxInfo
  on(actions.LoadTaxInfoAudit, (state, action) => ({
    ...state,

    taxInfoAudit: {
      ...state.taxInfoAudit,
      lastAuditLogId:
        state.taxInfoAudit.selectedId === action.taxInfoId
          ? state.taxInfoAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.TaxInfoAuditLoaded, (state, action) => ({
    ...state,

    taxInfoAudit: {
      ...state.taxInfoAudit,
      ...(!state.taxInfoAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.taxInfoAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.taxInfoAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  // Passenger
  on(actions.LoadPassengerAudit, (state, action) => ({
    ...state,

    passengerAudit: {
      ...state.passengerAudit,
      lastAuditLogId:
        state.passengerAudit.selectedId === action.passengerId
          ? state.passengerAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PassengerAuditLoaded, (state, action) => ({
    ...state,

    passengerAudit: {
      ...state.passengerAudit,
      ...(!state.passengerAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.passengerAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.passengerAudit)),
      loaded: true,
      loading: false,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return personProfileReducer(state, action);
}
